/**
 * Phase 3E: Clarification Workflow Hook
 * 
 * Manages the complete clarification and surgeon confirmation workflow:
 * 1. Generate prompts for missing documentation elements
 * 2. Track responses (YES/NO/NA)
 * 3. Require final CPT-level confirmation
 * 4. Determine billable status
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CptDetectionResult } from '@/lib/reconciliation/detectionEngine';
import { 
  generateAllClarificationPrompts,
  GeneratedClarificationPrompt,
  canMarkBillable,
} from '@/lib/phase3/clarificationPromptGenerator';
import {
  ClarificationPrompt,
  ClarificationResponse,
  ClarificationStatus,
  SurgeonConfirmationV2,
  DocumentationStatusV2,
  BillableStatus,
} from '@/lib/phase3/types';
import { useDemoMode } from '@/contexts/DemoModeContext';

// ============================================================================
// TYPES
// ============================================================================

export interface ClarificationWorkflowState {
  prompts: ClarificationPrompt[];
  openPrompts: ClarificationPrompt[];
  resolvedPrompts: ClarificationPrompt[];
  loading: boolean;
  generating: boolean;
  error: Error | null;
}

export interface CptReconciliationState {
  cptCode: string;
  documentationStatus: DocumentationStatusV2;
  clarificationsResolved: boolean;
  surgeonConfirmation: SurgeonConfirmationV2 | null;
  billableStatus: BillableStatus;
  billableReason: string;
}

export interface ReconciliationProgress {
  totalCpts: number;
  cptsWithSufficientDocs: number;
  cptsWithClarificationsPending: number;
  cptsConfirmed: number;
  cptsBillable: number;
  progressPercent: number;
  allResolved: boolean;
  canExport: boolean;
}

interface UseClarificationWorkflowOptions {
  authorizationId: string | null;
  cptAnalysisResults: CptDetectionResult[];
  authorizedCpts: string[];
}

// ============================================================================
// DEMO DATA
// ============================================================================

function generateDemoPrompts(
  cptAnalysisResults: CptDetectionResult[],
  authorizationId: string
): ClarificationPrompt[] {
  const demoClarifications: ClarificationPrompt[] = [];
  
  for (const cptResult of cptAnalysisResults) {
    if (cptResult.documentationStatus === 'insufficient' || cptResult.documentationStatus === 'ambiguous') {
      for (const element of cptResult.elementResults) {
        if (element.strictness === 'required' && !element.matched) {
          demoClarifications.push({
            id: `demo-${cptResult.cptCode}-${element.elementId}`,
            case_id: authorizationId,
            cpt_code: cptResult.cptCode,
            requirement_id: element.elementId,
            prompt_text: `For CPT ${cptResult.cptCode}, payers typically require documentation of "${element.elementLabel}". Please confirm whether this was performed and documented.`,
            prompt_type: 'YES_NO',
            response: 'yes', // Auto-resolved in demo mode
            responded_by: 'demo-surgeon',
            responded_at: new Date().toISOString(),
            status: 'resolved',
            is_demo_entry: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      }
    }
  }
  
  return demoClarifications;
}

// ============================================================================
// HOOK
// ============================================================================

export function useClarificationWorkflow({
  authorizationId,
  cptAnalysisResults,
  authorizedCpts,
}: UseClarificationWorkflowOptions) {
  const { isDemoMode } = useDemoMode();
  
  const [state, setState] = useState<ClarificationWorkflowState>({
    prompts: [],
    openPrompts: [],
    resolvedPrompts: [],
    loading: true,
    generating: false,
    error: null,
  });
  
  const [cptStates, setCptStates] = useState<Record<string, CptReconciliationState>>({});

  // ============================================================================
  // FETCH EXISTING PROMPTS
  // ============================================================================
  
  const fetchPrompts = useCallback(async () => {
    if (!authorizationId) {
      setState(prev => ({ ...prev, prompts: [], loading: false }));
      return;
    }
    
    // Demo mode: return auto-resolved prompts
    if (isDemoMode) {
      const demoPrompts = generateDemoPrompts(cptAnalysisResults, authorizationId);
      setState({
        prompts: demoPrompts,
        openPrompts: demoPrompts.filter(p => p.status === 'open'),
        resolvedPrompts: demoPrompts.filter(p => p.status === 'resolved'),
        loading: false,
        generating: false,
        error: null,
      });
      return;
    }
    
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      const { data, error: fetchError } = await supabase
        .from('clarification_prompts')
        .select('*')
        .eq('case_id', authorizationId)
        .order('created_at', { ascending: true });
      
      if (fetchError) throw fetchError;
      
      const prompts: ClarificationPrompt[] = (data || []).map(row => ({
        id: row.id,
        case_id: row.case_id,
        cpt_code: row.cpt_code,
        requirement_id: row.requirement_id || undefined,
        prompt_text: row.prompt_text,
        prompt_type: row.prompt_type as 'YES_NO' | 'SELECT_ONE',
        response: row.response as ClarificationResponse | null,
        responded_by: row.responded_by || undefined,
        responded_at: row.responded_at || undefined,
        status: row.status as ClarificationStatus,
        resolution_note: row.resolution_note || undefined,
        is_demo_entry: row.is_demo_entry || false,
        created_at: row.created_at,
        updated_at: row.updated_at,
      }));
      
      setState({
        prompts,
        openPrompts: prompts.filter(p => p.status === 'open'),
        resolvedPrompts: prompts.filter(p => p.status === 'resolved' || p.status === 'dismissed'),
        loading: false,
        generating: false,
        error: null,
      });
    } catch (err) {
      console.error('Failed to fetch clarification prompts:', err);
      setState(prev => ({ ...prev, loading: false, error: err as Error }));
    }
  }, [authorizationId, cptAnalysisResults, isDemoMode]);
  
  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // ============================================================================
  // GENERATE PROMPTS FROM ANALYSIS RESULTS
  // ============================================================================
  
  const generatePrompts = useCallback(async (): Promise<boolean> => {
    if (!authorizationId || cptAnalysisResults.length === 0) return false;
    
    // In demo mode, prompts are auto-generated and resolved
    if (isDemoMode) {
      await fetchPrompts();
      return true;
    }
    
    setState(prev => ({ ...prev, generating: true }));
    
    try {
      // Generate prompts for CPTs needing clarification
      const { allPrompts } = generateAllClarificationPrompts(cptAnalysisResults);
      
      if (allPrompts.length === 0) {
        setState(prev => ({ ...prev, generating: false }));
        return true;
      }
      
      // Insert prompts into database
      const promptsToInsert = allPrompts.map(p => ({
        case_id: authorizationId,
        cpt_code: p.cptCode,
        requirement_id: p.requirementId,
        prompt_text: p.promptText,
        prompt_type: p.promptType,
        status: 'open',
        is_demo_entry: false,
      }));
      
      const { error: insertError } = await supabase
        .from('clarification_prompts')
        .upsert(promptsToInsert, {
          onConflict: 'case_id,cpt_code,requirement_id',
          ignoreDuplicates: true,
        });
      
      if (insertError) throw insertError;
      
      // Refresh prompts
      await fetchPrompts();
      return true;
    } catch (err) {
      console.error('Failed to generate clarification prompts:', err);
      setState(prev => ({ ...prev, generating: false, error: err as Error }));
      return false;
    }
  }, [authorizationId, cptAnalysisResults, isDemoMode, fetchPrompts]);

  // ============================================================================
  // RESPOND TO PROMPT
  // ============================================================================
  
  const respondToPrompt = useCallback(async (
    promptId: string,
    response: ClarificationResponse
  ): Promise<boolean> => {
    // Demo mode: update local state only
    if (isDemoMode) {
      setState(prev => {
        const updatedPrompts = prev.prompts.map(p =>
          p.id === promptId
            ? {
                ...p,
                response,
                status: 'resolved' as ClarificationStatus,
                responded_at: new Date().toISOString(),
              }
            : p
        );
        return {
          ...prev,
          prompts: updatedPrompts,
          openPrompts: updatedPrompts.filter(p => p.status === 'open'),
          resolvedPrompts: updatedPrompts.filter(p => p.status !== 'open'),
        };
      });
      return true;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error: updateError } = await supabase
        .from('clarification_prompts')
        .update({
          response,
          responded_by: user?.id,
          responded_at: new Date().toISOString(),
          status: 'resolved',
          updated_at: new Date().toISOString(),
        })
        .eq('id', promptId);
      
      if (updateError) throw updateError;
      
      await fetchPrompts();
      return true;
    } catch (err) {
      console.error('Failed to respond to prompt:', err);
      setState(prev => ({ ...prev, error: err as Error }));
      return false;
    }
  }, [isDemoMode, fetchPrompts]);

  // ============================================================================
  // DISMISS PROMPT
  // ============================================================================
  
  const dismissPrompt = useCallback(async (
    promptId: string,
    reason?: string
  ): Promise<boolean> => {
    if (isDemoMode) {
      setState(prev => {
        const updatedPrompts = prev.prompts.map(p =>
          p.id === promptId
            ? {
                ...p,
                status: 'dismissed' as ClarificationStatus,
                resolution_note: reason,
              }
            : p
        );
        return {
          ...prev,
          prompts: updatedPrompts,
          openPrompts: updatedPrompts.filter(p => p.status === 'open'),
          resolvedPrompts: updatedPrompts.filter(p => p.status !== 'open'),
        };
      });
      return true;
    }
    
    try {
      const { error: updateError } = await supabase
        .from('clarification_prompts')
        .update({
          status: 'dismissed',
          resolution_note: reason || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', promptId);
      
      if (updateError) throw updateError;
      
      await fetchPrompts();
      return true;
    } catch (err) {
      console.error('Failed to dismiss prompt:', err);
      setState(prev => ({ ...prev, error: err as Error }));
      return false;
    }
  }, [isDemoMode, fetchPrompts]);

  // ============================================================================
  // AUTO-RESOLVE ALL (DEMO MODE ONLY)
  // ============================================================================
  
  const autoResolveAll = useCallback(async (): Promise<boolean> => {
    if (!isDemoMode) {
      console.warn('autoResolveAll is only available in demo mode');
      return false;
    }
    
    // Mark all open prompts as resolved with YES
    setState(prev => {
      const updatedPrompts = prev.prompts.map(p => ({
        ...p,
        response: 'yes' as ClarificationResponse,
        status: 'resolved' as ClarificationStatus,
        responded_at: new Date().toISOString(),
        responded_by: 'demo-surgeon',
      }));
      return {
        ...prev,
        prompts: updatedPrompts,
        openPrompts: [],
        resolvedPrompts: updatedPrompts,
      };
    });
    
    return true;
  }, [isDemoMode]);

  // ============================================================================
  // CALCULATE PROGRESS
  // ============================================================================
  
  const progress = useMemo((): ReconciliationProgress => {
    const totalCpts = authorizedCpts.length;
    
    // Count CPTs by documentation status
    const cptsWithSufficientDocs = cptAnalysisResults.filter(
      r => r.documentationStatus === 'sufficient'
    ).length;
    
    // Count CPTs with pending clarifications
    const cptCodesWithOpenPrompts = new Set(
      state.openPrompts.map(p => p.cpt_code)
    );
    const cptsWithClarificationsPending = cptCodesWithOpenPrompts.size;
    
    // Count confirmed CPTs (from cptStates)
    const cptsConfirmed = Object.values(cptStates).filter(
      s => s.surgeonConfirmation === 'confirmed_performed'
    ).length;
    
    // Count billable CPTs
    const cptsBillable = Object.values(cptStates).filter(
      s => s.billableStatus === 'billable'
    ).length;
    
    // Calculate progress
    const resolvedCount = cptsWithSufficientDocs + 
      (state.resolvedPrompts.length > 0 ? cptsWithClarificationsPending : 0);
    const progressPercent = totalCpts > 0 
      ? Math.round((cptsConfirmed / totalCpts) * 100) 
      : 0;
    
    const allResolved = state.openPrompts.length === 0;
    const canExport = cptsBillable > 0 && allResolved;
    
    return {
      totalCpts,
      cptsWithSufficientDocs,
      cptsWithClarificationsPending,
      cptsConfirmed,
      cptsBillable,
      progressPercent,
      allResolved,
      canExport,
    };
  }, [authorizedCpts.length, cptAnalysisResults, state.openPrompts, state.resolvedPrompts, cptStates]);

  // ============================================================================
  // UPDATE CPT STATE
  // ============================================================================
  
  const updateCptConfirmation = useCallback((
    cptCode: string,
    confirmation: SurgeonConfirmationV2
  ) => {
    const cptResult = cptAnalysisResults.find(r => r.cptCode === cptCode);
    const docStatus = cptResult?.documentationStatus || 'not_found';
    
    // Check if clarifications for this CPT are resolved
    const cptPrompts = state.prompts.filter(p => p.cpt_code === cptCode);
    const allCptClarificationsResolved = cptPrompts.every(
      p => p.status === 'resolved' || p.status === 'dismissed'
    );
    
    // Determine if billable
    const { canBill, reason } = canMarkBillable(
      true, // is authorized
      docStatus,
      confirmation,
      allCptClarificationsResolved
    );
    
    setCptStates(prev => ({
      ...prev,
      [cptCode]: {
        cptCode,
        documentationStatus: docStatus,
        clarificationsResolved: allCptClarificationsResolved,
        surgeonConfirmation: confirmation,
        billableStatus: canBill ? 'billable' : 'not_billable',
        billableReason: reason,
      },
    }));
  }, [cptAnalysisResults, state.prompts]);

  // ============================================================================
  // GET PROMPTS FOR CPT
  // ============================================================================
  
  const getPromptsForCpt = useCallback((cptCode: string): ClarificationPrompt[] => {
    return state.prompts.filter(p => p.cpt_code === cptCode);
  }, [state.prompts]);
  
  const hasOpenPromptsForCpt = useCallback((cptCode: string): boolean => {
    return state.openPrompts.some(p => p.cpt_code === cptCode);
  }, [state.openPrompts]);

  return {
    // State
    ...state,
    cptStates,
    progress,
    
    // Actions
    fetchPrompts,
    generatePrompts,
    respondToPrompt,
    dismissPrompt,
    autoResolveAll,
    updateCptConfirmation,
    
    // Helpers
    getPromptsForCpt,
    hasOpenPromptsForCpt,
  };
}
